import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { describeError } from "../api/http";
import {
  useMarket,
  usePlan,
  useRecommendSquad,
  useRecommendTransfers,
  useSettings,
  useSquad,
  useUpdatePlan,
  useUpdateSettings,
} from "../api/queries";
import type { Metrics, Move, Weights } from "../api/types";
import { Comparador, type ComparadorRow } from "../components/Comparador";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { Page } from "../components/layout/Page";
import { Pitch, type PitchPlayer } from "../components/Pitch";
import { Stat } from "../components/Scoreline";
import { Button, ButtonLink } from "../components/ui/Button";
import { PositionChip, ToggleChip } from "../components/ui/Chip";
import { EmptyState, ErrorState } from "../components/ui/ErrorState";
import { Skeleton } from "../components/ui/Skeleton";
import { useToast } from "../components/ui/Toast";
import {
  decimal,
  delta,
  money,
  percent,
  percentDelta,
  points,
  pointsDelta,
} from "../lib/format";
import { addToPlan, toPlanDraft, type PlanDraft } from "../lib/plan";

/**
 * Pantalla *Recomendar* — M8-T05 (UC-08.05).
 *
 * QUÉ HACE
 *   Deja tocar los cuatro pesos del modelo y el número de movimientos, pide a
 *   `POST /api/recommend/squad` la secuencia de fichajes que más sube el objetivo de la plantilla
 *   (sección 7.3) y la enseña **justificada**: cada cambio con su comparador saliente → entrante,
 *   el once que quedaría y un botón que lo vuelca todo al plan de una vez.
 *
 * POR QUÉ LOS PESOS VIAJAN EN EL CUERPO Y ADEMÁS SE GUARDAN
 *   Son dos cosas distintas y las dos hacen falta. En el **cuerpo** (`weights`) sobrescriben los
 *   ajustes solo para esa llamada, que es lo que permite probar «¿y si la eficiencia pesara más?»
 *   sin recalcular la tabla de métricas de toda la aplicación. Y **al soltar** el control se
 *   guardan en *Ajustes* con `useUpdateSettings` —el mismo hook y la misma ruta de guardado que la
 *   pantalla *Ajustes* (M8-T07): aquí no hay un segundo formulario— para que la próxima
 *   recomendación, y lo que enseñan *Plantilla* y *Mercado*, partan de esa misma mezcla.
 *
 *   `maxMoves` es la excepción: se queda en la pantalla. Guardarlo dispararía el guardado entero
 *   de `PUT /api/settings` por un número que solo afecta a la siguiente petición.
 *
 * DE DÓNDE SALEN LAS MÉTRICAS DEL COMPARADOR
 *   `Move` solo trae `PlayerRef` (id, nombre, posición, equipo y precio): ni una métrica. Y sin
 *   métricas el comparador de la sección 8.4 no se puede dibujar, que es el primer punto de la
 *   aceptación. Se arma un índice con lo que las consultas normales ya traen —mi plantilla
 *   (`GET /api/squad`, de donde salen los salientes), la plantilla resultante que devuelve la
 *   propia recomendación y el mercado (`GET /api/market`, de donde salen los entrantes)— y se
 *   busca ahí. Si a un jugador no se le encuentran métricas, la tarjeta enseña su explicación y
 *   sus dos cifras, pero no finge un comparador con ceros.
 *
 * LAS ALTERNATIVAS SE AGRUPAN POR QUIÉN ENTRA
 *   `planTransfers` devuelve las alternativas como los siguientes de la lista ordenada del primer
 *   movimiento (sección 7.3.5), así que con una plantilla homogénea las cinco pueden ser **el
 *   mismo fichaje** vendiendo a cinco jugadores intercambiables. El motor no se toca: se agrupan
 *   al pintarlas, con la mejor de cada fichaje delante y las demás salidas nombradas debajo.
 *
 * UNA SOLA ACCIÓN PRIMARIA A LA VEZ
 *   Antes de que haya resultado la acción de la pantalla es *Recomendar*. En cuanto lo hay, la
 *   primaria pasa a ser *Aplicar al plan* y *Recomendar* se queda en secundaria: el verbo no
 *   cambia (sección 8.3), cambia el peso visual.
 */

/* ========================================================= precios de un movimiento ====== */

/**
 * A cuánto se vende el saliente: a su precio de hoy, que es con lo que cuenta el optimizador
 * (sección 7.3.3). `null` cuando el movimiento no vende a nadie.
 */
export function precioDeVenta(move: Move): number | null {
  return move.out === undefined ? null : Math.round(move.out.price);
}

/**
 * A cuánto se ficha el entrante. **No es `move.in.price`**: ese es el precio del catálogo, y lo
 * que de verdad se paga es el precio de la venta en el mercado o el de la cláusula, que puede ser
 * el doble. La cifra exacta se despeja de `deltaBudget`, que el servidor calcula con el precio de
 * compra de verdad:
 *
 *   · `swap`: `deltaBudget = precio(saliente) − compra(entrante)`
 *   · `buy` : `deltaBudget = −compra(entrante)`
 *
 * Así el plan queda con las cifras con las que se hizo la cuenta, sin pedir el mercado otra vez.
 * Si algo no cuadrara —un `deltaBudget` que despejara un precio imposible— se cae al del catálogo
 * antes que meter un número negativo en el plan.
 */
export function precioDeCompra(move: Move): number | null {
  if (move.in === undefined) return null;
  const venta = precioDeVenta(move) ?? 0;
  const despejado = Math.round(venta - move.deltaBudget);
  return Number.isFinite(despejado) && despejado >= 0
    ? despejado
    : Math.round(move.in.price);
}

/**
 * El plan con todos los movimientos volcados dentro: cada saliente a las ventas y cada entrante a
 * las compras. Se aplica sobre el plan que ya hubiera —*Aplicar al plan* añade, no sustituye— y
 * `addToPlan` se encarga de que nadie acabe en los dos lados a la vez.
 */
export function planConMovimientos(
  base: PlanDraft,
  moves: readonly Move[],
): PlanDraft {
  let plan = base;
  for (const move of moves) {
    const venta = precioDeVenta(move);
    if (move.out !== undefined && venta !== null) {
      plan = addToPlan(plan, "sells", { playerId: move.out.id, price: venta });
    }
    const compra = precioDeCompra(move);
    if (move.in !== undefined && compra !== null) {
      plan = addToPlan(plan, "buys", { playerId: move.in.id, price: compra });
    }
  }
  return plan;
}

/* ================================================================ el comparador ========== */

/**
 * Las cuatro métricas con las que la sección 8.4 justifica un cambio. Cada una con su formato:
 * la disponibilidad y la tendencia son proporciones, no puntos, y escritas como puntos no
 * significarían nada.
 */
export function filasDelComparador(
  sale: Metrics | null | undefined,
  entra: Metrics | null | undefined,
): ComparadorRow[] {
  if (sale === null || sale === undefined) return [];
  if (entra === null || entra === undefined) return [];
  return [
    {
      label: "Pts esperados",
      out: sale.expectedPpr,
      in: entra.expectedPpr,
      format: points,
    },
    {
      label: "Disponibilidad",
      out: sale.availability,
      in: entra.availability,
      format: (valor) => percent(valor),
    },
    {
      label: "Eficiencia",
      out: sale.efficiency,
      in: entra.efficiency,
      format: (valor) => decimal(valor),
    },
    {
      label: "Tendencia",
      out: sale.trend,
      in: entra.trend,
      format: (valor) => percentDelta(valor),
    },
  ];
}

/* ================================================================== las alternativas ===== */

export interface AlternativaAgrupada {
  /** La mejor del grupo: la primera que llegó, que es la de mayor Δobjetivo. */
  move: Move;
  /** Los demás salientes con los que ese mismo fichaje aparecía, en orden. */
  otrasSalidas: string[];
}

/**
 * Una entrada por jugador que entra. Sin agrupar, cinco alternativas pueden ser cinco tarjetas
 * con el mismo fichaje y distinto saliente: cinco veces la misma información y ninguna decisión.
 */
export function agruparAlternativas(
  moves: readonly Move[],
): AlternativaAgrupada[] {
  const grupos = new Map<string, AlternativaAgrupada>();

  for (const move of moves) {
    const clave =
      move.in === undefined
        ? `sale-${String(move.out?.id ?? 0)}`
        : `entra-${String(move.in.id)}`;
    const previo = grupos.get(clave);
    if (previo === undefined) {
      grupos.set(clave, { move, otrasSalidas: [] });
      continue;
    }
    if (move.out !== undefined) previo.otrasSalidas.push(move.out.name);
  }

  return [...grupos.values()];
}

/* ========================================================================== vocabulario == */

/** Factor 0–1 con dos decimales: `0,20`. El mismo formato que los deslizadores de *Ajustes*. */
function factor(valor: number): string {
  return valor.toFixed(2).replace(".", ",");
}

function entero(valor: number): string {
  return String(Math.round(valor));
}

/** Lo que hace cada movimiento, dicho en una palabra. */
const TITULO_DEL_TIPO: Record<Move["type"], string> = {
  swap: "Cambio",
  buy: "Fichaje",
  sell: "Venta",
};

/** Cuántos movimientos como mucho puede pedir la pantalla. */
export const TOPE_DE_MOVIMIENTOS = 6;

/** Cuántas alternativas se piden al planificador (sección 7.3.5). */
export const ALTERNATIVAS = 5;

/**
 * Lado de la ficha redonda en la cabecera de un movimiento. Más pequeña que la de las listas (38):
 * aquí van dos caras y dos chips en una sola línea, y a 38 la cabecera se parte en dos.
 */
const AVATAR_DEL_MOVIMIENTO = 28;

/** A partir de aquí la espera se anuncia por escrito (sección 8.2). */
export const AVISO_DE_ESPERA_MS = 1_000;

/* ============================================================================== piezas === */

interface TarjetaProps {
  titulo?: string;
  children: ReactNode;
  className?: string;
}

function Tarjeta({ titulo, children, className }: TarjetaProps) {
  return (
    <section
      className={clsx(
        "grid gap-3 rounded-xl border border-border bg-surface p-3",
        className,
      )}
    >
      {titulo === undefined ? null : (
        <h2 className="text-[15px] leading-none text-ink">{titulo}</h2>
      )}
      {children}
    </section>
  );
}

interface DeslizadorProps {
  id: string;
  etiqueta: string;
  valor: number;
  min: number;
  max: number;
  paso: number;
  formato: (valor: number) => string;
  onChange: (valor: number) => void;
  /** Se llama al soltar el control: al levantar el dedo, al soltar la tecla o al salir. */
  onSoltar?: (valor: number) => void;
  hint?: string;
}

/**
 * Un parámetro acotado. Igual que el de *Ajustes*, con una diferencia: aquí el valor se **guarda
 * al soltar**, no al arrastrar. Guardar en cada píxel dispararía un `PUT /api/settings` cincuenta
 * veces por gesto, y con él el recálculo de las métricas.
 */
function Deslizador({
  id,
  etiqueta,
  valor,
  min,
  max,
  paso,
  formato,
  onChange,
  onSoltar,
  hint,
}: DeslizadorProps) {
  const hintId = `${id}-hint`;

  function soltar(): void {
    onSoltar?.(valor);
  }

  return (
    <div className="grid gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[13px] text-ink">
          {etiqueta}
        </label>
        <span className="font-mono text-[13px] tabular-nums text-muted">
          {formato(valor)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={paso}
        value={valor}
        onChange={(evento) => {
          onChange(Number(evento.target.value));
        }}
        onMouseUp={soltar}
        onTouchEnd={soltar}
        onKeyUp={soltar}
        onBlur={soltar}
        aria-describedby={hint === undefined ? undefined : hintId}
        className="h-touch w-full accent-pitch"
      />
      {hint === undefined ? null : (
        <span id={hintId} className="text-xs text-muted">
          {hint}
        </span>
      )}
    </div>
  );
}

/** La silueta de los controles mientras llegan los ajustes (sección 8.2). */
function EsqueletoDeControles() {
  return (
    <Tarjeta titulo="Pesos del modelo">
      <Skeleton
        variant="text"
        count={5}
        className="gap-6"
        label="Cargando los pesos del modelo"
      />
      <Skeleton variant="row" label="Cargando" />
    </Tarjeta>
  );
}

/** La silueta del resultado: la franja de cifras, dos movimientos y el campo (sección 8.2). */
function EsqueletoDelResultado() {
  return (
    <>
      <Skeleton variant="row" label="Calculando la recomendación" />
      <Skeleton variant="card" count={2} label="Calculando los movimientos" />
      <Skeleton variant="pitch" label="Calculando el once resultante" />
    </>
  );
}

interface FichaDeMovimientoProps {
  move: Move;
  filas: ComparadorRow[];
  children?: ReactNode;
}

/**
 * Un movimiento, entero: quién sale y quién entra, las dos cifras del cambio, la frase que lo
 * explica y el comparador que enseña de dónde sale la mejora. El Δ agregado nunca va solo.
 */
function FichaDeMovimiento({ move, filas, children }: FichaDeMovimientoProps) {
  const sube = move.deltaBudget >= 0;

  return (
    <article className="grid gap-2 rounded-xl border border-border bg-surface p-3">
      <h3 className="flex flex-wrap items-center gap-2 text-[14px] font-medium leading-tight">
        <span className="sr-only">{TITULO_DEL_TIPO[move.type]}: </span>
        {move.out === undefined ? null : (
          <>
            <PlayerAvatar
              playerId={move.out.id}
              name={move.out.name}
              size={AVATAR_DEL_MOVIMIENTO}
            />
            <PositionChip position={move.out.position} />
            <span>{move.out.name}</span>
          </>
        )}
        {move.out !== undefined && move.in !== undefined ? (
          <span className="text-muted">→</span>
        ) : null}
        {move.in === undefined ? null : (
          <>
            <PlayerAvatar
              playerId={move.in.id}
              name={move.in.name}
              size={AVATAR_DEL_MOVIMIENTO}
            />
            <PositionChip position={move.in.position} />
            <span>{move.in.name}</span>
          </>
        )}
      </h3>

      <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[13px] tabular-nums">
        <span className="text-up">
          {pointsDelta(move.deltaObjective)} pts/jorn.
        </span>
        {/* El signo va escrito: el color confirma, no informa por su cuenta. */}
        <span className={sube ? "text-up" : "text-money"}>
          {delta(move.deltaBudget)}
        </span>
      </div>

      <p className="text-xs text-muted">{move.explanation}</p>

      {filas.length > 0 ? (
        <Comparador
          rows={filas}
          outName={move.out?.name ?? "sale"}
          inName={move.in?.name ?? "entra"}
        />
      ) : null}

      {children}
    </article>
  );
}

/* ========================================================== la pestaña de alternativas === */

interface AlternativasProps {
  consulta: ReturnType<typeof useRecommendTransfers>;
  filasDe: (move: Move) => ComparadorRow[];
  onReintentar: () => void;
}

/**
 * Las cinco mejores alternativas al primer movimiento (sección 7.3.5), agrupadas por quién entra.
 * No son «el plan B entero»: son otras maneras de dar el primer paso.
 */
function Alternativas({ consulta, filasDe, onReintentar }: AlternativasProps) {
  if (consulta.isPending) {
    return (
      <Skeleton variant="card" count={3} label="Calculando las alternativas" />
    );
  }
  if (consulta.isError) {
    return (
      <ErrorState
        title="No se han podido calcular las alternativas"
        message={describeError(consulta.error)}
        onRetry={onReintentar}
      />
    );
  }

  const grupos = agruparAlternativas(consulta.data?.alternatives ?? []);
  if (grupos.length === 0) {
    return (
      <EmptyState
        title="No hay alternativas"
        message="Ningún otro movimiento supera la mejora mínima, así que no hay con qué comparar el primero."
      />
    );
  }

  return (
    <>
      <h2 className="text-[16px] leading-none">
        Otras maneras de dar el primer paso
      </h2>
      {grupos.map((grupo) => (
        <FichaDeMovimiento
          key={`${String(grupo.move.in?.id ?? 0)}-${String(grupo.move.out?.id ?? 0)}`}
          move={grupo.move}
          filas={filasDe(grupo.move)}
        >
          {grupo.otrasSalidas.length > 0 ? (
            <p className="text-xs text-muted">
              El mismo fichaje sale a cuenta vendiendo a{" "}
              {grupo.otrasSalidas.join(", ")}.
            </p>
          ) : null}
        </FichaDeMovimiento>
      ))}
    </>
  );
}

/* ============================================================================= pantalla == */

interface Controles {
  wForm: number;
  alpha: number;
  beta: number;
  gamma: number;
  maxMoves: number;
}

export function Recommend() {
  const { showToast } = useToast();
  const ajustes = useSettings();
  const guardarAjustes = useUpdateSettings();
  const plan = usePlan();
  const guardarPlan = useUpdatePlan();
  const recomendar = useRecommendSquad();
  const alternativas = useRecommendTransfers();

  const [controles, setControles] = useState<Controles | null>(null);
  const [partirDelPlan, setPartirDelPlan] = useState(true);
  const [vista, setVista] = useState<"recomendacion" | "alternativas">(
    "recomendacion",
  );
  const [tardando, setTardando] = useState(false);
  const sembrado = useRef(false);

  const datos = ajustes.data;

  /**
   * Los ajustes guardados son el punto de partida de los controles, **una sola vez**. Después
   * manda la pantalla: si se volvieran a sembrar con cada respuesta del servidor, la respuesta de
   * un peso recién guardado borraría el que se estuviera moviendo mientras tanto.
   */
  useEffect(() => {
    if (sembrado.current || datos === undefined) return;
    sembrado.current = true;
    setControles({
      wForm: datos.weights.wForm,
      alpha: datos.weights.alpha,
      beta: datos.weights.beta,
      gamma: datos.weights.gamma,
      maxMoves: Math.min(TOPE_DE_MOVIMIENTOS, datos.recommender.maxMoves),
    });
  }, [datos]);

  /** «Calculando…» solo si la espera se nota: por debajo del segundo basta el esqueleto. */
  useEffect(() => {
    if (!recomendar.isPending) {
      setTardando(false);
      return;
    }
    const aviso = setTimeout(() => {
      setTardando(true);
    }, AVISO_DE_ESPERA_MS);
    return () => {
      clearTimeout(aviso);
    };
  }, [recomendar.isPending]);

  const pesos: Weights | null =
    controles === null
      ? null
      : {
          wForm: controles.wForm,
          alpha: controles.alpha,
          beta: controles.beta,
          gamma: controles.gamma,
        };

  /* ------------------------------------------------------- métricas para el comparador -- */

  const miPlantilla = useSquad(partirDelPlan);
  const mercado = useMarket({}, partirDelPlan);

  const metricas = useMemo(() => {
    const indice = new Map<number, Metrics | null>();
    for (const jugador of mercado.data?.listings ?? []) {
      indice.set(jugador.id, jugador.metrics);
    }
    for (const jugador of recomendar.data?.squad ?? []) {
      indice.set(jugador.id, jugador.metrics);
    }
    // Mi plantilla va la última: es la fuente más fiable de quien sale.
    for (const jugador of miPlantilla.data?.players ?? []) {
      indice.set(jugador.id, jugador.metrics);
    }
    return indice;
  }, [mercado.data, recomendar.data, miPlantilla.data]);

  function filasDe(move: Move): ComparadorRow[] {
    if (move.out === undefined || move.in === undefined) return [];
    return filasDelComparador(
      metricas.get(move.out.id),
      metricas.get(move.in.id),
    );
  }

  /* ----------------------------------------------------------------------- las acciones - */

  function pedirRecomendacion(): void {
    if (pesos === null || controles === null) return;
    setVista("recomendacion");
    recomendar.mutate({
      plan: partirDelPlan,
      maxMoves: controles.maxMoves,
      weights: pesos,
    });
    // Las alternativas salen de otra ruta, que no acepta pesos de prueba (sección 9.1): se piden
    // a la vez para que la pestaña no tenga que esperar a que la abran.
    alternativas.mutate({ plan: partirDelPlan, k: ALTERNATIVAS });
  }

  /** Guarda un peso en *Ajustes* al soltar, y solo si de verdad ha cambiado. */
  function guardarPeso(cambio: Partial<Weights>): void {
    const guardados = datos?.weights;
    if (guardados === undefined) return;
    const distinto = Object.entries(cambio).some(
      ([clave, valor]) => guardados[clave as keyof Weights] !== valor,
    );
    if (!distinto) return;
    guardarAjustes.mutate({ weights: cambio });
  }

  const resultado = recomendar.data;
  const movimientos = resultado?.moves ?? [];
  const planLeido = plan.data !== undefined;

  function aplicarAlPlan(): void {
    if (!planLeido || movimientos.length === 0) return;
    guardarPlan.mutate(
      planConMovimientos(toPlanDraft(plan.data), movimientos),
      {
        onSuccess: () => {
          showToast("Aplicado al plan");
        },
      },
    );
  }

  /* ------------------------------------------------------------------------ el resultado - */

  const entrantes = useMemo(
    () =>
      new Set(
        movimientos.flatMap((move) =>
          move.in === undefined ? [] : [move.in.id],
        ),
      ),
    [movimientos],
  );

  const once: PitchPlayer[] = (resultado?.xi.starters ?? []).map((slot) => ({
    id: slot.player.id,
    name: slot.player.name,
    position: slot.player.position,
    expected: slot.expected,
    // Los cuatro estados de la sección 8.4: aquí solo se distingue a quien acaba de entrar.
    state: entrantes.has(slot.player.id) ? "in" : "normal",
  }));

  let cuerpo: ReactNode;
  if (recomendar.isPending) {
    cuerpo = (
      <>
        {tardando ? (
          <p role="status" className="text-sm text-muted">
            Calculando…
          </p>
        ) : null}
        <EsqueletoDelResultado />
      </>
    );
  } else if (recomendar.isError) {
    cuerpo = (
      <ErrorState
        title="No se ha podido calcular la recomendación"
        message={describeError(recomendar.error)}
        onRetry={pedirRecomendacion}
      />
    );
  } else if (resultado === undefined) {
    cuerpo = (
      <EmptyState
        title="Todavía no hay ninguna recomendación"
        message="Ajusta los pesos si quieres y pulsa Recomendar para ver qué cambios mejorarían tu plantilla."
      />
    );
  } else if (vista === "alternativas") {
    cuerpo = (
      <Alternativas
        consulta={alternativas}
        filasDe={filasDe}
        onReintentar={() => {
          alternativas.mutate({ plan: partirDelPlan, k: ALTERNATIVAS });
        }}
      />
    );
  } else {
    const mejora = resultado.objectiveAfter - resultado.objectiveBefore;
    cuerpo = (
      <>
        <Tarjeta>
          <div className="flex flex-wrap justify-between gap-3">
            <Stat
              label="Objetivo antes"
              value={points(resultado.objectiveBefore)}
              size={26}
            />
            <Stat
              label="Después"
              value={points(resultado.objectiveAfter)}
              tone={mejora > 0 ? "up" : "neutral"}
              caption={
                mejora > 0 ? `${pointsDelta(mejora)} pts/jorn.` : undefined
              }
              size={26}
            />
            <Stat
              label="Presupuesto"
              value={money(resultado.budgetAfter)}
              tone="money"
              size={26}
            />
          </div>
        </Tarjeta>

        {movimientos.length === 0 ? (
          <EmptyState
            title="No hay ningún movimiento que mejore"
            message={
              resultado.reasons.length > 0 ? (
                <span className="grid gap-1">
                  {resultado.reasons.map((motivo) => (
                    <span key={motivo}>{motivo}</span>
                  ))}
                </span>
              ) : (
                "Con los pesos actuales, ninguna operación supera la mejora mínima. Prueba a subir el peso de la eficiencia o a revisar el mercado más tarde."
              )
            }
            action={
              <ButtonLink to="/mercado" variant="primary">
                Ver mercado
              </ButtonLink>
            }
          />
        ) : (
          <>
            <h2 className="text-[16px] leading-none">Movimientos</h2>
            {movimientos.map((move, indice) => (
              <FichaDeMovimiento
                key={`${move.type}-${String(move.out?.id ?? 0)}-${String(move.in?.id ?? 0)}-${String(indice)}`}
                move={move}
                filas={filasDe(move)}
              />
            ))}

            <Tarjeta
              titulo={
                resultado.xi.formation === ""
                  ? "Once resultante"
                  : `Once resultante · ${resultado.xi.formation}`
              }
            >
              {once.length === 0 ? (
                <p className="text-xs text-muted">
                  Con esta plantilla no se completa ninguna formación permitida,
                  así que no hay once que enseñar.
                </p>
              ) : (
                <Pitch
                  size="mini"
                  formation={resultado.xi.formation}
                  players={once}
                />
              )}
            </Tarjeta>

            <Button
              variant="primary"
              block
              disabled={!planLeido || guardarPlan.isPending}
              onClick={aplicarAlPlan}
            >
              Aplicar al plan
            </Button>
            {guardarPlan.isError ? (
              <p role="alert" className="text-xs text-down">
                {describeError(guardarPlan.error)}
              </p>
            ) : null}
          </>
        )}
      </>
    );
  }

  /* ----------------------------------------------------------------------- los controles - */

  const hayResultado = resultado !== undefined;
  const entradasDelPlan =
    (plan.data?.buys.length ?? 0) + (plan.data?.sells.length ?? 0);

  let panel: ReactNode;
  if (ajustes.isPending) {
    panel = <EsqueletoDeControles />;
  } else if (controles === null) {
    panel = (
      <ErrorState
        title="No se han podido cargar los ajustes"
        message={describeError(ajustes.error)}
        onRetry={() => void ajustes.refetch()}
      />
    );
  } else {
    panel = (
      <Tarjeta titulo="Pesos del modelo">
        <Deslizador
          id="peso-alpha"
          etiqueta="Puntos esperados (α)"
          valor={controles.alpha}
          min={0}
          max={1}
          paso={0.05}
          formato={factor}
          onChange={(valor) => {
            setControles({ ...controles, alpha: valor });
          }}
          onSoltar={(valor) => {
            guardarPeso({ alpha: valor });
          }}
        />
        <Deslizador
          id="peso-beta"
          etiqueta="Eficiencia por euro (β)"
          valor={controles.beta}
          min={0}
          max={1}
          paso={0.05}
          formato={factor}
          onChange={(valor) => {
            setControles({ ...controles, beta: valor });
          }}
          onSoltar={(valor) => {
            guardarPeso({ beta: valor });
          }}
        />
        <Deslizador
          id="peso-gamma"
          etiqueta="Tendencia de valor (γ)"
          valor={controles.gamma}
          min={0}
          max={1}
          paso={0.05}
          formato={factor}
          onChange={(valor) => {
            setControles({ ...controles, gamma: valor });
          }}
          onSoltar={(valor) => {
            guardarPeso({ gamma: valor });
          }}
        />
        <Deslizador
          id="peso-forma"
          etiqueta="Peso de la forma"
          valor={controles.wForm}
          min={0}
          max={1}
          paso={0.05}
          formato={factor}
          onChange={(valor) => {
            setControles({ ...controles, wForm: valor });
          }}
          onSoltar={(valor) => {
            guardarPeso({ wForm: valor });
          }}
          hint="0 es solo la media de temporada; 1, solo las últimas jornadas."
        />
        <Deslizador
          id="movimientos-maximos"
          etiqueta="Movimientos máximos"
          valor={controles.maxMoves}
          min={1}
          max={TOPE_DE_MOVIMIENTOS}
          paso={1}
          formato={entero}
          onChange={(valor) => {
            setControles({ ...controles, maxMoves: valor });
          }}
        />

        <div className="flex min-h-touch items-center justify-between gap-3">
          {/* La etiqueta ocupa la fila entera: 44 px de alto por todo el ancho (sección 8.1). */}
          <label
            htmlFor="partir-del-plan"
            className="flex min-h-touch flex-1 cursor-pointer items-center text-[13px] font-medium text-ink"
          >
            Partir del plan
          </label>
          <input
            id="partir-del-plan"
            type="checkbox"
            checked={partirDelPlan}
            onChange={(evento) => {
              setPartirDelPlan(evento.target.checked);
            }}
            aria-describedby="partir-del-plan-hint"
            className="h-6 w-11 shrink-0 accent-pitch"
          />
        </div>
        <p id="partir-del-plan-hint" className="-mt-2 text-xs text-muted">
          {entradasDelPlan > 0
            ? "Parte de la plantilla y el presupuesto que dejaría tu plan, no de los de hoy."
            : "Tu plan está vacío, así que da lo mismo: se parte de la plantilla de hoy."}
        </p>

        {/* El verbo no cambia mientras calcula (sección 8.3): lo que dice que está en marcha es
            el botón deshabilitado y el esqueleto que aparece debajo. */}
        <Button
          variant={hayResultado ? "secondary" : "primary"}
          block
          disabled={recomendar.isPending}
          aria-busy={recomendar.isPending}
          onClick={pedirRecomendacion}
        >
          Recomendar
        </Button>
        <p className="text-xs text-muted">
          Los pesos se guardan en Ajustes al soltar el control.
          {guardarAjustes.isError ? (
            <>
              {" "}
              <span className="text-down">
                {describeError(guardarAjustes.error)}
              </span>
            </>
          ) : null}
        </p>
      </Tarjeta>
    );
  }

  return (
    <Page title="Recomendar">
      {panel}

      {hayResultado || recomendar.isPending ? (
        <div
          role="group"
          aria-label="Qué se muestra"
          className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <ToggleChip
            pressed={vista === "recomendacion"}
            onClick={() => {
              setVista("recomendacion");
            }}
          >
            Recomendación
          </ToggleChip>
          <ToggleChip
            pressed={vista === "alternativas"}
            onClick={() => {
              setVista("alternativas");
            }}
          >
            Alternativas
          </ToggleChip>
        </div>
      ) : null}

      {cuerpo}
    </Page>
  );
}
